import React, { useState, useEffect } from 'react';
import { Form, Select, Button, Input, Spin, notification, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { SearchParams } from '../types';
import { fetchSymbols, getAvailableTimeframes, searchSymbols } from '../services/cryptoService';
import './SearchForm.css';
import { useI18n } from '../i18n/I18nProvider';

const { Option } = Select;

// 主流币种：放到组件外，避免 useEffect 依赖抖动
const MAIN_COINS: string[] = [
  'BTC/USDT', 'ETH/USDT', 'SOL/USDT',
  'XRP/USDT', 'DOGE/USDT', 'ADA/USDT',
  'AVAX/USDT', 'DOT/USDT', 'SHIB/USDT'
];

interface SearchFormProps {
  onSearch: (params: SearchParams) => void;
  loading: boolean;
}

const SearchForm: React.FC<SearchFormProps> = ({ onSearch, loading }) => {
  const { t } = useI18n();
  const [form] = Form.useForm();
  const [symbols, setSymbols] = useState<string[]>([]);
  const [timeframes, setTimeframes] = useState<string[]>([]);
  const [loadingSymbols, setLoadingSymbols] = useState(false);
  const [searchResults, setSearchResults] = useState<string[]>([]);

  // 页面加载时获取交易对和时间周期
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoadingSymbols(true);
      try {
        // 获取交易对列表
        const symbolList = await fetchSymbols();
        // 确保主流币种在列表中，使用Array.from代替扩展操作符
        const combinedArray = [...MAIN_COINS, ...symbolList];
        const uniqueSymbols = Array.from(new Set(combinedArray));
        setSymbols(uniqueSymbols);
        setSearchResults(uniqueSymbols);
        
        // 获取时间周期列表
        const timeframeList = getAvailableTimeframes();
        setTimeframes(timeframeList);
      } catch (error) {
        notification.error({
          message: t('common.error'),
          description: t('search.noDataDesc'),
        });
        console.error('初始数据加载失败:', error);
        // 至少加载主流币种
        setSymbols(MAIN_COINS);
        setSearchResults(MAIN_COINS);
      } finally {
        setLoadingSymbols(false);
      }
    };

    fetchInitialData();
  }, [t]);

  // 处理交易对搜索
  const handleSymbolSearch = async (value: string) => {
    if (!value) {
      setSearchResults(symbols);
      return;
    }

    try {
      setLoadingSymbols(true);
      
      // 搜索API和主流币种
      const apiResults = await searchSymbols(value);
      
      setSearchResults(apiResults);
      
      if (apiResults.length === 0) {
        message.info(t('search.noMatch'));
      }
    } catch (error) {
      console.error('搜索交易对失败:', error);
      // 错误时至少返回匹配的主流币种
      const fallbackMatches = MAIN_COINS.filter(coin => 
        coin.toLowerCase().includes(value.toLowerCase())
      );
      setSearchResults(fallbackMatches.length > 0 ? fallbackMatches : MAIN_COINS);
    } finally {
      setLoadingSymbols(false);
    }
  };

  // 提交表单
  const handleSubmit = (values: any) => {
    onSearch({
      symbol: values.symbol,
      timeframe: values.timeframe,
      limit: values.limit ? parseInt(values.limit) : 500,
    });
  };
  

  return (
    <div className="search-form">
      <h2>{t('search.title')}</h2>

      
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="inline"
        initialValues={{ symbol: 'BTC/USDT', timeframe: '1h', limit: 500 }}
      >
        <div className="form-row">
          <Form.Item
            name="symbol"
            label={t('search.pair')}
            rules={[{ required: true, message: t('search.pair') }]}
          >
            <Select
              showSearch
              placeholder={t('search.pairPlaceholder')}
              optionFilterProp="children"
              loading={loadingSymbols}
              style={{ width: 150 }}
              onSearch={handleSymbolSearch}
              filterOption={false}
              defaultActiveFirstOption={false}
              notFoundContent={loadingSymbols ? <Spin size="small" /> : t('search.noMatch')}
              showArrow={true}
              listHeight={300}
            >
              {searchResults.map(symbol => (
                <Option key={symbol} value={symbol}>
                  {symbol}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="timeframe"
            label={t('search.timeframe')}
            rules={[{ required: true, message: t('search.timeframe') }]}
          >
            <Select placeholder="选择时间周期" style={{ width: 100 }}>
              {timeframes.map(timeframe => (
                <Option key={timeframe} value={timeframe}>
                  {timeframe}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="limit" label={t('search.limit')}>
            <Input placeholder="数量限制" type="number" style={{ width: 100 }} />
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              icon={<SearchOutlined />} 
              loading={loading}
            >
              {t('search.search')}
            </Button>
          </Form.Item>
        </div>
      </Form>
      
      {loading && (
        <div className="loading-container">
          <Spin spinning={true}>
            <div style={{ padding: '50px', textAlign: 'center' }}>
              {t('search.loading')}
            </div>
          </Spin>
        </div>
      )}
    </div>
  );
};

export default SearchForm; 